import { css } from '@emotion/core';
import styled from '@emotion/styled';
import {
  Avatar,
  List as MUIList,
  ListItemIcon,
  ListItemText as _ListItemText,
  MenuItem as _MenuItem,
} from '@material-ui/core';
import { List } from 'immutable';
import { ClassAttributes, createElement, SyntheticEvent } from 'react';

import AmazonAdAccount from '../../../entity/AmazonAdAccount';
import theme from '../../../theme';
import { SPACING_REGULAR } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import mq from '../../styleUtility/mediaquery';

interface Props {
  amazonAdAccounts: List<AmazonAdAccount>;
  selected: AmazonAdAccount | null;
  onClick: (e: SyntheticEvent<HTMLElement>, selected: AmazonAdAccount) => void;
  className?: string;
}

const AmazonAdAccountList = ({
  amazonAdAccounts,
  selected,
  onClick,
  className,
}: ClassAttributes<HTMLElement> & Props) => {
  const selectedListItemPrimaryStyle = css({
    color: theme.palette.primary.main,
  }).toString();

  const selectedListItemSecondaryStyle = css({
    color: theme.palette.primary.light,
  }).toString();

  const accounts = amazonAdAccounts.map((adAccount) => {
    const isSelected = selected ? selected.profileId === adAccount.profileId : false;

    const adAccountName = adAccount.name ? adAccount.name : 'Amazon Ads Account';
    const thumbnail = (
      <ListItemIcon>
        <Avatar>{adAccountName.toUpperCase().charAt(0)}</Avatar>
      </ListItemIcon>
    );

    const Account = isSelected ? (
      <ListItemText
        primary={`${adAccountName}${adAccount.countryCode ? ` (${adAccount.countryCode})` : ''}`}
        classes={{
          primary: selectedListItemPrimaryStyle,
          secondary: selectedListItemSecondaryStyle,
        }}
      />
    ) : (
      <ListItemText primary={`${adAccountName}${adAccount.countryCode ? ` (${adAccount.countryCode})` : ''}`} />
    );

    return (
      <MenuItem selected={isSelected} key={adAccount.profileId} onClick={(e) => onClick(e, adAccount)}>
        {thumbnail}
        {Account}
      </MenuItem>
    );
  });

  return <Root className={className}>{accounts}</Root>;
};

const Root = styled(MUIList)({
  height: '100%',
  maxHeight: '635px',
  overflowY: 'scroll',
  border: `1px solid ${theme.palette.grey['200']}`,
  [mq('sp')]: {
    maxHeight: 'none',
    overflowY: 'auto',
  },
});

const MenuItem = styled(_MenuItem)(
  flexibleLayoutContainer({
    direction: 'row',
    wrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
  }),
  {
    height: 48,
    [mq('sp')]: {
      height: 'auto',
    },
  },
);

const ListItemText = styled(_ListItemText)({
  marginRight: SPACING_REGULAR,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  [mq('sp')]: {
    whiteSpace: 'initial',
    wordBreak: 'break-word',
    marginRight: 0,
    paddingRight: 0,
    '& > span': {
      lineHeight: 1.1,
    },
    '& > p': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
});

export default AmazonAdAccountList;
